import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, X, Loader2 } from "lucide-react";

const useLevel = (stream) => {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!stream?.getAudioTracks().length) return undefined;
    const ac = new (window.AudioContext || window.webkitAudioContext)();
    const an = ac.createAnalyser(); an.fftSize = 256;
    ac.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.frequencyBinCount);
    let raf;
    const tick = () => { an.getByteFrequencyData(buf); setLevel(Math.min(1, buf.reduce((a, b) => a + b, 0) / buf.length / 60)); raf = requestAnimationFrame(tick); };
    tick();
    return () => { cancelAnimationFrame(raf); ac.close(); };
  }, [stream]);
  return level;
};

export const LobbyPreview = ({ onClose, title = "Check your camera & mic" }) => {
  const vid = useRef(null);
  const [stream, setStream] = useState(null);
  const [err, setErr] = useState("");
  const [cam, setCam] = useState(true);
  const [mic, setMic] = useState(true);
  const level = useLevel(mic ? stream : null);
  useEffect(() => {
    let s;
    navigator.mediaDevices?.getUserMedia({ video: true, audio: { echoCancellation: true } })
      .then(st => { s = st; setStream(st); if (vid.current) vid.current.srcObject = st; })
      .catch(e => setErr(e.name === "NotAllowedError" ? "Camera/mic access is blocked. Click the lock icon in your browser's address bar and allow them." : "We couldn't find a camera or microphone on this device."));
    return () => s?.getTracks().forEach(t => t.stop());
  }, []);
  const toggle = (kind, on, set) => { stream?.getTracks().filter(t => t.kind === kind).forEach(t => { t.enabled = !on; }); set(!on); };
  return (
    <div className="bg-slate-900 rounded-xl p-4 text-white mt-3" data-testid="lobby-preview">
      <div className="flex items-center mb-3"><p className="font-semibold flex-1 text-sm">{title}</p>{onClose && <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="lobby-close-btn"><X className="w-4 h-4" /></button>}</div>
      <div className="relative aspect-video bg-black rounded-lg overflow-hidden max-w-md">
        <video ref={vid} autoPlay muted playsInline className={`w-full h-full object-contain bg-slate-900 ${cam ? "" : "invisible"}`} data-testid="lobby-video" />
        {!stream && !err && <Loader2 className="w-6 h-6 animate-spin absolute inset-0 m-auto" />}
        {err && <p className="absolute inset-0 flex items-center justify-center text-center text-sm p-4 text-amber-300" data-testid="lobby-error">{err}</p>}
        {!cam && stream && <p className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Camera off</p>}
      </div>
      <div className="flex items-center gap-3 mt-3 max-w-md">
        <button onClick={() => toggle("audio", mic, setMic)} disabled={!stream} className={`w-10 h-10 rounded-full flex items-center justify-center ${mic ? "bg-slate-700" : "bg-red-600"}`} data-testid="lobby-mic-btn">{mic ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}</button>
        <button onClick={() => toggle("video", cam, setCam)} disabled={!stream} className={`w-10 h-10 rounded-full flex items-center justify-center ${cam ? "bg-slate-700" : "bg-red-600"}`} data-testid="lobby-cam-btn">{cam ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}</button>
        <div className="flex-1">
          <p className="text-[11px] text-slate-400 mb-1">{mic ? "Say something — the bar should move" : "Mic muted"}</p>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden" data-testid="lobby-mic-meter"><div className="h-full bg-green-400 transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} /></div>
        </div>
      </div>
    </div>
  );
};
